import { CustomCellRendererProps } from "@ag-grid-community/react";
import { Intent } from "@blueprintjs/core";
import { HexId } from "@hex/common";
import React, { useCallback, useState } from "react";
import styled from "styled-components";

import { HexButton } from "../../../../hex-components/HexButton.js";
import { HexPopover } from "../../../../hex-components/index.js";
import { useGetCategoriesAndStatusesQuery } from "../../../../hooks/useProjectLabelsForHex.generated.js";
import { MinimalStatus } from "../../../../hooks/useProjectLabelsForHex.js";
import { ORG_ID } from "../../../../orgs.js";
import { StatusLabel } from "../../../common/labels/StatusLabel.js";
import { useToaster } from "../../../common/Toasts.js";
import { StatusPickerPopover } from "../../../home/home-project-list/selection-actions/StatusPickerPopover.js";
import { useUpdateHexSelectionStatusMutation } from "../../../home/home-project-list/selection-actions/StatusSelectionButton.generated.js";
import { PulseIcon } from "../../../icons/CustomIcons.js";
import { ProjectsTableRowData } from "../../types.js";

export interface EditableStatusTableCellRendererProps {
  data: ProjectsTableRowData | undefined;
  hexId: HexId;
  node: CustomCellRendererProps<ProjectsTableRowData>["node"];
  value: MinimalStatus | null | undefined;
}

export const EditableStatusTableCellRenderer =
  React.memo<EditableStatusTableCellRendererProps>(
    function EditableStatusTableCellRenderer({ data, hexId, node, value }) {
      const toaster = useToaster();
      const [isOpen, setIsOpen] = useState(false);
      const [newStatus, setNewStatus] = useState<
        MinimalStatus | null | undefined
      >(undefined);
      const [statusLoading, setStatusLoading] = useState(false);

      const { data: statusData } = useGetCategoriesAndStatusesQuery({
        variables: { orgId: ORG_ID },
        fetchPolicy: "cache-first",
      });

      const [updateHexStatus] = useUpdateHexSelectionStatusMutation();

      const handleCancel = useCallback(() => {
        setNewStatus(undefined);
        setIsOpen(false);
      }, []);

      const handleSave = useCallback(async () => {
        try {
          setStatusLoading(true);
          const res = await updateHexStatus({
            variables: { hexIds: [hexId], statusId: newStatus?.id ?? null },
          });

          if (res.data == null) {
            throw new Error("No data returned");
          }

          const updatedHex = res.data.batchUpdateHexStatus[0];
          if (updatedHex && data != null && data.__typename !== "UnknownHex") {
            // Update the AG Grid row data
            node.updateData({
              ...data,
              status: updatedHex.status,
            });
          }

          setIsOpen(false);
          toaster.show({
            intent: Intent.SUCCESS,
            message: "Status updated",
          });
        } catch (err) {
          console.error(err);
          toaster.show({
            intent: Intent.DANGER,
            message: "Failed to update status",
          });
        } finally {
          setStatusLoading(false);
        }
      }, [hexId, newStatus?.id, updateHexStatus, data, node, toaster]);

      const handlePopoverInteraction = useCallback(
        (nextOpenState: boolean) => {
          if (!statusLoading) {
            setIsOpen(nextOpenState);
          }
        },
        [statusLoading],
      );

      const statuses = statusData?.orgById.statuses ?? [];

      return (
        <StatusCellContainer>
          <HexPopover
            content={
              <StatusPickerPopover
                handleCancel={handleCancel}
                handleSave={handleSave}
                initialStatus={isOpen ? (value ?? null) : undefined}
                loading={statusLoading}
                newStatus={newStatus}
                setNewStatus={setNewStatus}
                statuses={statuses}
              />
            }
            isOpen={isOpen}
            minimal={true}
            position="bottom-left"
            onInteraction={handlePopoverInteraction}
          >
            <StatusButton
              disabled={statusLoading}
              hasStatus={value != null}
              loading={statusLoading}
              minimal={true}
            >
              {value != null ? (
                <StatusLabel muted={!value.endorsed} status={value} />
              ) : (
                <AddStatusPlaceholder>
                  <PulseIcon />
                  <span>Add status</span>
                </AddStatusPlaceholder>
              )}
            </StatusButton>
          </HexPopover>
        </StatusCellContainer>
      );
    },
  );

const StatusCellContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const StatusButton = styled(HexButton)<{ hasStatus: boolean }>`
  &&&&&&&&& {
    /* Visually align with non-editable status */
    margin-left: -2px;
    padding: 4px 2px;
    min-height: unset;
    border-radius: 4px;

    &:hover {
      background-color: ${({ theme }) => theme.hoverColor};
    }
  }
`;

const AddStatusPlaceholder = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 2px;
  color: ${({ theme }) => theme.fontColor.MUTED};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  opacity: 0;
  transition: opacity ${({ theme }) => theme.animation.duration}
    ${({ theme }) => theme.animation.easing};

  .ag-row-hover & {
    opacity: 1;
  }
`;
